<script setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useConfig } from 'dashboard/composables/useConfig';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useStore } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useCaptainConfigStore } from 'dashboard/store/captain/preferences';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import FeaturePaywall from '../components/FeaturePaywall.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import ModelSelector from './components/ModelSelector.vue';
import FeatureToggle from './components/FeatureToggle.vue';

const { t } = useI18n();
const store = useStore();
const { captainEnabled } = useCaptain();
const { isEnterprise, enterprisePlanName } = useConfig();
const { isOnChatipsCloud } = useAccount();
const { isFeatureFlagEnabled } = usePolicy();

const captainConfigStore = useCaptainConfigStore();
const { uiFlags, accountOpenaiIntegrated } = storeToRefs(captainConfigStore);

const isBehindAPaywall = computed(
  () => !isFeatureFlagEnabled(FEATURE_FLAGS.CAPTAIN)
);

const isLoading = computed(
  () => !isBehindAPaywall.value && uiFlags.value.isFetching
);

const modelFeatures = computed(() => [
  {
    key: 'editor',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.EDITOR.TITLE'),
    description: t('CAPTAIN_SETTINGS.MODEL_CONFIG.EDITOR.DESCRIPTION'),
  },
  {
    key: 'assistant',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.ASSISTANT.TITLE'),
    description: t('CAPTAIN_SETTINGS.MODEL_CONFIG.ASSISTANT.DESCRIPTION'),
    featureFlag: FEATURE_FLAGS.CAPTAIN_V2,
  },
  {
    key: 'copilot',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.COPILOT.TITLE'),
    description: t('CAPTAIN_SETTINGS.MODEL_CONFIG.COPILOT.DESCRIPTION'),
    featureFlag: FEATURE_FLAGS.CAPTAIN_V2,
  },
]);

const featureToggles = computed(() => [
  {
    key: 'label_suggestion',
    featureFlag: FEATURE_FLAGS.CAPTAIN_TASKS,
  },
  {
    key: 'help_center_search',
    featureFlag: FEATURE_FLAGS.HELP_CENTER_EMBEDDING_SEARCH,
  },
  {
    key: 'audio_transcription',
  },
]);

const shouldShowFeature = feature => {
  if (feature.featureFlag) return true;

  if (isOnChatipsCloud.value && captainEnabled) {
    return true;
  }

  if (feature.enterprise) {
    return isEnterprise;
  }

  return true;
};

const isFeatureAccessible = feature => {
  if (feature.featureFlag) {
    return isFeatureFlagEnabled(feature.featureFlag);
  }

  if (isOnChatipsCloud.value && captainEnabled) {
    return true;
  }

  if (feature.enterprise) {
    return isEnterprise && enterprisePlanName === 'enterprise';
  }

  return true;
};

async function handleFeatureToggle({ feature, enabled }) {
  try {
    await captainConfigStore.updatePreferences({
      captain_features: { [feature]: enabled },
    });
    await store.dispatch('accounts/get', { silent: true });
    useAlert(t('CAPTAIN_SETTINGS.API.SUCCESS'));
  } catch (error) {
    useAlert(t('CAPTAIN_SETTINGS.API.ERROR'));
    captainConfigStore.fetch();
  }
}

async function handleModelChange({ feature, model }) {
  try {
    await captainConfigStore.updatePreferences({
      captain_models: { [feature]: model },
    });
    useAlert(t('CAPTAIN_SETTINGS.API.SUCCESS'));
  } catch (error) {
    useAlert(t('CAPTAIN_SETTINGS.API.ERROR'));
    captainConfigStore.fetch();
  }
}

watch(
  isBehindAPaywall,
  paywalled => {
    if (!paywalled) captainConfigStore.fetch();
  },
  { immediate: true }
);
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :no-records-message="t('CAPTAIN_SETTINGS.NOT_ENABLED')"
    :loading-message="t('CAPTAIN_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('CAPTAIN_SETTINGS.TITLE')"
        :description="t('CAPTAIN_SETTINGS.DESCRIPTION')"
        :link-text="t('CAPTAIN_SETTINGS.LINK_TEXT')"
        icon-name="captain"
        feature-name="captain_billing"
      />
    </template>
    <template #body>
      <FeaturePaywall v-if="isBehindAPaywall" feature-prefix="CAPTAIN" />
      <div v-else class="flex flex-col gap-1">
        <!-- Model Configuration Section -->
        <SectionLayout
          v-if="!accountOpenaiIntegrated"
          :title="t('CAPTAIN_SETTINGS.MODEL_CONFIG.TITLE')"
          :description="t('CAPTAIN_SETTINGS.MODEL_CONFIG.DESCRIPTION')"
        >
          <div class="grid gap-4">
            <ModelSelector
              v-for="feature in modelFeatures"
              v-show="shouldShowFeature(feature)"
              :key="feature.key"
              :is-allowed="isFeatureAccessible(feature)"
              :feature-key="feature.key"
              :title="feature.title"
              :description="feature.description"
              @change="handleModelChange"
            />
          </div>
        </SectionLayout>
        <p v-else class="text-sm text-n-slate-11 px-4">
          {{ t('CAPTAIN_SETTINGS.MODEL_CONFIG.ACCOUNT_OPENAI_HIDDEN') }}
        </p>

        <!-- Features Section -->
        <SectionLayout
          :title="t('CAPTAIN_SETTINGS.FEATURES.TITLE')"
          :description="t('CAPTAIN_SETTINGS.FEATURES.DESCRIPTION')"
          with-border
        >
          <div class="grid gap-4">
            <FeatureToggle
              v-for="feature in featureToggles"
              v-show="shouldShowFeature(feature)"
              :key="feature.key"
              :is-allowed="isFeatureAccessible(feature)"
              :feature-key="feature.key"
              @change="handleFeatureToggle"
              @model-change="handleModelChange"
            />
          </div>
        </SectionLayout>
      </div>
    </template>
  </SettingsLayout>
</template>
